import {
  ActivityLog,
  type Event,
  type Property,
  QuoteDocument,
  QuoteLine,
  type ServicesRendered,
  type TokenProposalData,
  type User,
} from '@kalos/kalos-rpc';
import { NewSendProposalDocumentRequest, NULL_TIME } from '@kalos/kalos-rpc';
import { Dialog, DialogContent, DialogHeader, DialogTitle, LoadingIcon, toast } from '@kalos/ui';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useProposalCreateTokenMutation } from '../../hooks/react-query/useProposalQuery';
import {
  useCreateQuoteDocumentMutation,
  useUpdateQuoteDocumentMutation,
} from '../../hooks/react-query/useQuoteDocumentQuery';
import {
  useCreateQuoteLineMutation,
  useDeleteQuoteLineMutation,
  useQuoteLineQuery,
  useUpdateQuoteLineMutation,
} from '../../hooks/react-query/useQuoteLineQuery';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { ActivityLogClientService, ProposalClientService, timestamp } from '../../tools/helpers';
import { getFileName } from '../ComponentsLibrary/ServiceCall/components/utils';
import { TAB_VALUE_TO_INDEX_MAP } from '../ComponentsLibrary/ServiceCall/tabUtils';
import { ProposalQuickUpdate } from './components/ProposalQuickUpdate';
import {
  ProposalReviewAndSend,
  type ProposalReviewAndSendFormData,
} from './components/ProposalReviewAndSendStep';
import { ProposalStatus } from './components/ProposalStatusCard';
import { ProposalWorkflow } from './components/ProposalWorkflow';
import { ReviewStep } from './components/ReviewStep';
import { ServicesStep } from './components/ServicesStep';
import { useProposalContext } from './context/ProposalContext';
interface ClientInfoData {
  displayName?: string;
  withJobNotes: boolean;
  notes?: string;
  includeAttachments: boolean;
  preparedById?: number;
  displayBusinessName: boolean;
  validUntilDays: number; // Number of days until proposal expires
  validUntilDate?: string; // Date when agreement will expire
  quoteExpireDate?: string; // Date when proposal expires
  blockLineItems?: boolean; // Block line item creation after proposal is signed
}

interface ProposalProps {
  serviceItem: Event;
  property: Property;
  customer: User;
  servicesRendered: ServicesRendered[];
  className?: string;
  reload?: () => void;
  initialStep?: 'services' | 'review' | 'settings';
  /** Called when job/event fields (other than quote-document pricing) are updated */
  onEventUpdate?: (updates: Partial<Event>) => void;
}

export const ProposalModule: FC<ProposalProps> = ({
  serviceItem,
  property,
  customer,
  servicesRendered,
  className,
  reload,
  initialStep = 'services',
}) => {
  const [currentStep, setCurrentStep] = useState<'services' | 'review' | 'settings'>(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isServicesValid, setIsServicesValid] = useState(false);
  const [localServiceItem, setLocalServiceItem] = useState<Event>(serviceItem);

  // Sync from parent when serviceItem changes (e.g. after load, or from another tab)
  useEffect(() => {
    setLocalServiceItem(serviceItem);
  }, [serviceItem]);
  const [isQuickUpdateOpen, setIsQuickUpdateOpen] = useState(false);
  const [isPhotosBusy, setIsPhotosBusy] = useState(false);

  const loggedUser = useAuth();
  const isProposalAdmin = useMemo(
    () => !!loggedUser?.user?.permissionGroups?.find((p) => p.name === 'ProposalAdmin'),
    [loggedUser?.user?.permissionGroups],
  );
  const createTokenMutation = useProposalCreateTokenMutation();
  const {
    quoteDocument,
    isLoading: isContextLoading,
    isQuoteDocumentLoading: isQuoteDocLoading,
    refreshQuoteDocument,
  } = useProposalContext();

  // Get cached preference for blocking line items
  const [blockLineItemsPreference, setBlockLineItemsPreference] = useLocalStorage(
    'proposalBlockLineItemsDefault',
    false,
  );

  const createQuoteDocumentMutation = useCreateQuoteDocumentMutation();
  const updateQuoteDocumentMutation = useUpdateQuoteDocumentMutation();

  // Persist discount on the quote document (job/event updated on proposal approval)
  const handleDiscountChange = useCallback(
    async (newDiscount: string, newDiscountCost: string) => {
      if (!quoteDocument?.id) {
        toast({
          title: 'Error',
          description: 'Quote document is not ready yet. Try again in a moment.',
          variant: 'destructive',
        });
        return;
      }

      try {
        await updateQuoteDocumentMutation.mutateAsync(
          QuoteDocument.create({
            id: quoteDocument.id,
            discount: newDiscount,
            discountcost: newDiscountCost,
            fieldMask: ['Discount', 'Discountcost'],
          }),
        );
        await refreshQuoteDocument();
      } catch (err) {
        console.error('Error updating discount:', err);
        toast({
          title: 'Error',
          description: 'Failed to update discount.',
          variant: 'destructive',
        });
      }
    },
    [quoteDocument?.id, updateQuoteDocumentMutation, refreshQuoteDocument],
  );

  // Quote line mutations
  const createQuoteLineMutation = useCreateQuoteLineMutation();
  const updateQuoteLineMutation = useUpdateQuoteLineMutation();
  const deleteQuoteLineMutation = useDeleteQuoteLineMutation();

  // Fetch quote lines: when a quote document exists, scope by quoteDocumentId (align with JobDetails);
  // before doc exists, fetch by jobNumber so lines added on services step are visible.
  const { data: quoteLineData, isLoading: isQuoteLineLoading } = useQuoteLineQuery({
    filters: {
      isActive: 1,
      ...(quoteDocument?.id
        ? { quoteDocumentId: quoteDocument.id }
        : { jobNumber: serviceItem.id.toString() }),
      includeProposalPhotos: true,
      withoutLimit: true,
    },
    enabled: true,
  });

  // Get quote lines from query data
  const quoteLines = useMemo(() => quoteLineData?.results || [], [quoteLineData]);

  // Check if there are any approved line items (quoteStatus === 1)
  const hasApprovedLineItems = useMemo(
    () => quoteLines.some((line) => line.quoteStatus === 1),
    [quoteLines],
  );

  // Handler for adding a new quote line (align with JobDetails: associate with quote document when present)
  const handleAddQuoteLine = useCallback(
    async (quoteLine: QuoteLine) => {
      try {
        const newQuoteLine = QuoteLine.create({
          ...quoteLine,
          isActive: 1,
          warranty: 2,
          jobNumber: serviceItem.id.toString(),
          forUser: customer.id,
          ...(quoteDocument?.id && { quoteDocumentId: quoteDocument.id }),
        });

        return await createQuoteLineMutation.mutateAsync(newQuoteLine);
      } catch (err) {
        console.error('Error adding quote line:', err);
        toast({
          title: 'Error',
          description: 'Failed to add service.',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [createQuoteLineMutation, customer.id, serviceItem.id, quoteDocument?.id],
  );

  // Handler for updating a quote line
  const handleUpdateQuoteLine = useCallback(
    async (quoteLine: QuoteLine) => {
      try {
        const quoteUpdate = QuoteLine.create({
          ...quoteLine,
          fieldMask:
            (quoteLine.fieldMask?.length ?? 0) > 0
              ? quoteLine.fieldMask
              : ['Description', 'Adjustment', 'IsActive', 'QuotedQuantity'],
        });

        return await updateQuoteLineMutation.mutateAsync(quoteUpdate);
      } catch (err) {
        console.error('Error updating quote line:', err);
        toast({
          title: 'Error',
          description: 'Failed to update service.',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [updateQuoteLineMutation],
  );

  // Handler for deleting a quote line
  const handleDeleteQuoteLine = useCallback(
    async (quoteLine: QuoteLine) => {
      try {
        await deleteQuoteLineMutation.mutateAsync(quoteLine);
      } catch (err) {
        console.error('Error deleting quote line:', err);
        toast({
          title: 'Error',
          description: 'Failed to remove service.',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [deleteQuoteLineMutation],
  );

  // Process services rendered to create initial job notes
  const filteredServicesRendered = servicesRendered.filter(
    (sr) => sr.serviceRendered !== '' && sr.serviceRendered != null,
  );

  const concatServicesRendered = filteredServicesRendered.reduce((concatedString, sr) => {
    const date = sr.timeStarted ? new Date(sr.timeStarted).toLocaleDateString() : '';
    return concatedString + `${date}-${sr.name}-${sr.serviceRendered} \n`;
  }, '');

  // Client information state
  const [clientInfo, setClientInfo] = useState<ClientInfoData>({
    // Initialize without displayName since it will be calculated based on customer and displayBusinessName
    withJobNotes: false,
    notes: concatServicesRendered,
    includeAttachments: false,
    preparedById: undefined,
    displayBusinessName: false,
    validUntilDays: 7, // Default 7 days
    validUntilDate: `${new Date().toISOString().split('T')[0]} 00:00:00`, // Default to today
    quoteExpireDate: `${format(addDays(new Date(), 1), 'yyyy-MM-dd')} 00:00:00`, // Default to tomorrow
    blockLineItems: blockLineItemsPreference, // Initialize from cached preference
  });

  // File name for the proposal document
  const fileName = getFileName({
    serviceItemId: serviceItem.id,
    customerId: customer.id,
  });

  // Sync clientInfo from context quote document
  useEffect(() => {
    if (quoteDocument) {
      let validUntilDays = 7;
      if (quoteDocument.quoteExpireDate) {
        const expireDate = new Date(quoteDocument.quoteExpireDate);
        const now = new Date();
        const diffTime = expireDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        validUntilDays = diffDays > 0 ? diffDays : 7;
      }
      setClientInfo((prevInfo) => ({
        ...prevInfo,
        notes: quoteDocument.jobNotes || prevInfo.notes,
        withJobNotes: quoteDocument.includeJobNotes == 1,
        includeAttachments: quoteDocument.includeAttachments === 1,
        preparedById: quoteDocument.preparedById || undefined,
        displayBusinessName: quoteDocument.displayBusinessName === 1,
        validUntilDays,
        validUntilDate:
          quoteDocument.validUntilDate || `${new Date().toISOString().split('T')[0]} 00:00:00`,
        quoteExpireDate:
          quoteDocument.quoteExpireDate ||
          `${format(addDays(new Date(), 1), 'yyyy-MM-dd')} 00:00:00`,
        blockLineItems: quoteDocument.blockLineItems === 1 ? true : prevInfo.blockLineItems,
      }));
    }
  }, [quoteDocument]);

  // Handler for client info changes
  // Persist ONLY the proposal expiration date for the active proposal. Kept
  // separate from handleClientInfoChange so editing one date (or switching to the
  // other linked proposal) never rewrites the sibling value from a stale snapshot.
  const handleExpirationDateChange = async (value: string) => {
    if (!quoteDocument?.id) return;
    const previous = clientInfo.quoteExpireDate;
    setClientInfo((prev) => ({ ...prev, quoteExpireDate: value }));
    try {
      await updateQuoteDocumentMutation.mutateAsync(
        QuoteDocument.create({
          id: quoteDocument.id,
          eventId: quoteDocument.eventId,
          quoteExpireDate: value,
          fieldMask: ['QuoteExpireDate'],
        }),
      );
      await refreshQuoteDocument();
    } catch (err) {
      setClientInfo((prev) => ({ ...prev, quoteExpireDate: previous }));
      throw err;
    }
  };

  // Persist ONLY the agreement valid-until (length of agreement) date for the
  // active proposal, for the same reason as handleExpirationDateChange.
  const handleValidUntilDateChange = async (value: string) => {
    if (!quoteDocument?.id) return;
    const previous = clientInfo.validUntilDate;
    setClientInfo((prev) => ({ ...prev, validUntilDate: value }));
    try {
      await updateQuoteDocumentMutation.mutateAsync(
        QuoteDocument.create({
          id: quoteDocument.id,
          eventId: quoteDocument.eventId,
          validUntilDate: value,
          fieldMask: ['ValidUntilDate'],
        }),
      );
      await refreshQuoteDocument();
    } catch (err) {
      setClientInfo((prev) => ({ ...prev, validUntilDate: previous }));
      throw err;
    }
  };

  const handleClientInfoChange = async (info: ClientInfoData) => {
    // Use the displayName if provided, otherwise calculate it based on customer and displayBusinessName
    const displayName =
      info.displayName ||
      (info.displayBusinessName
        ? customer.businessname || `${customer.firstname} ${customer.lastname}`
        : `${customer.firstname} ${customer.lastname}`);

    // Save blockLineItems preference to localStorage when it changes
    // Use current value if undefined is passed (e.g., from ReviewStep)
    const blockLineItemsValue = info.blockLineItems ?? clientInfo.blockLineItems ?? false;
    if (blockLineItemsValue !== clientInfo.blockLineItems) {
      setBlockLineItemsPreference(blockLineItemsValue);
    }

    try {
      // Only update the fields that are present in info
      const fieldMask: string[] = [
        'JobNotes',
        'PreparedById',
        'DisplayBusinessName',
        'IncludeJobNotes',
        'IncludeAttachments',
        'BlockLineItems',
      ];
      const updateObj: any = {
        ...quoteDocument,
        jobNotes: info.notes !== undefined ? info.notes : clientInfo.notes,
        preparedById: info.preparedById || clientInfo.preparedById || 0,
        displayBusinessName: info.displayBusinessName ? 1 : 0,
        includeJobNotes: info.withJobNotes ? 1 : 0,
        includeAttachments: info.includeAttachments ? 1 : 0,
        blockLineItems: blockLineItemsValue ? 1 : 0,
      };
      // NOTE: expiration / valid-until dates are intentionally NOT written here.
      // They are persisted individually by handleExpirationDateChange /
      // handleValidUntilDateChange so a settings save never drags a stale date
      // value along and reverts a change made on this or the other linked proposal.
      updateObj.fieldMask = fieldMask;

      if (quoteDocument) {
        const updatedQuoteDoc = QuoteDocument.create(updateObj);
        const result = await updateQuoteDocumentMutation.mutateAsync(updatedQuoteDoc);
        if (result) {
          await refreshQuoteDocument();
          // Update clientInfo with the returned data
          setClientInfo({
            ...clientInfo,
            ...info,
            displayName,
            notes:
              result.jobNotes !== undefined
                ? result.jobNotes
                : info.notes !== undefined
                  ? info.notes
                  : clientInfo.notes,
            withJobNotes: result.includeJobNotes == 1,
            includeAttachments: result.includeAttachments === 1,
            preparedById: result.preparedById || info.preparedById,
            displayBusinessName: result.displayBusinessName === 1,
            validUntilDate:
              result.validUntilDate || info.validUntilDate || clientInfo.validUntilDate,
            quoteExpireDate:
              result.quoteExpireDate || info.quoteExpireDate || clientInfo.quoteExpireDate,
            blockLineItems: result.blockLineItems === 1,
          });
        }
      } else {
        // Creating a new document
        const createObj: any = {
          eventId: serviceItem.id,
          propertyId: serviceItem.propertyId,
          jobNotes: info.notes !== undefined ? info.notes : '',
          preparedById: info.preparedById || 0,
          displayBusinessName: info.displayBusinessName ? 1 : 0,
          includeJobNotes: info.withJobNotes ? 1 : 0,
          includeAttachments: info.includeAttachments ? 1 : 0,
          blockLineItems: blockLineItemsValue ? 1 : 0,
          discount: serviceItem.discount ?? '0',
          discountcost: serviceItem.discountcost ?? '0',
        };
        if (info.validUntilDate) createObj.validUntilDate = info.validUntilDate;
        if (info.quoteExpireDate) createObj.quoteExpireDate = info.quoteExpireDate;
        const createdDoc = await createQuoteDocumentMutation.mutateAsync(
          QuoteDocument.create(createObj),
        );
        if (createdDoc) {
          await refreshQuoteDocument();
          // Update clientInfo with the returned data
          setClientInfo({
            ...clientInfo,
            ...info,
            displayName,
            notes:
              createdDoc.jobNotes !== undefined
                ? createdDoc.jobNotes
                : info.notes !== undefined
                  ? info.notes
                  : clientInfo.notes,
            withJobNotes: createdDoc.includeJobNotes == 1,
            includeAttachments: createdDoc.includeAttachments === 1,
            preparedById: createdDoc.preparedById || info.preparedById,
            displayBusinessName: createdDoc.displayBusinessName === 1,
            validUntilDate:
              createdDoc.validUntilDate || info.validUntilDate || clientInfo.validUntilDate,
            quoteExpireDate:
              createdDoc.quoteExpireDate || info.quoteExpireDate || clientInfo.quoteExpireDate,
            blockLineItems: createdDoc.blockLineItems === 1,
          });
        }
      }
    } catch (err) {
      console.error('Error saving to QuoteDocument:', err);
    }
  };

  // Move to next step
  const moveToNextStep = async () => {
    if (currentStep === 'services') {
      if (!isServicesValid) {
        toast({
          title: 'Validation Error',
          description: 'Please add at least one service with a valid price.',
          variant: 'destructive',
        });
        return;
      }

      // If moving to review step and quote document doesn't exist, create it
      if (!quoteDocument) {
        setIsSubmitting(true);
        try {
          await handleClientInfoChange(clientInfo);
        } catch (err) {
          console.error('Error creating quote document:', err);
          toast({
            title: 'Error',
            description: 'Failed to initialize quote document. Please try again.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        } finally {
          setIsSubmitting(false);
        }
      }

      setCurrentStep('review');
    } else if (currentStep === 'review') {
      setCurrentStep('settings');
    }
  };

  // Move to previous step
  const moveToPreviousStep = () => {
    if (currentStep === 'settings') {
      setCurrentStep('review');
    } else if (currentStep === 'review') {
      setCurrentStep('services');
    }
  };

  // Handler for proposal submission
  const handleSubmitProposal = async (data: ProposalReviewAndSendFormData, inPerson?: boolean) => {
    if (!quoteDocument) {
      toast({
        title: 'Error',
        description: 'No quote document found. Please add services first.',
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);

    try {
      // Check if expiration date is today and adjust to tomorrow if needed
      let adjustedExpirationDate = clientInfo.quoteExpireDate;
      if (clientInfo.quoteExpireDate) {
        const expirationDate = parseISO(clientInfo.quoteExpireDate);
        const today = new Date();
        if (isSameDay(expirationDate, today)) {
          adjustedExpirationDate = format(addDays(today, 1), 'yyyy-MM-dd 00:00:00');
        }
      }

      // Create token for proposal
      const tokenData: TokenProposalData = {
        jobId: serviceItem.id,
        quoteDocumentId: quoteDocument.id,
        expiringDate: new Date(Date.now() + clientInfo.validUntilDays * 24 * 60 * 60 * 1000)
          .toISOString()
          .replace('T', ' ')
          .replace(/\.\d+Z$/, ''),
        purpose: '',
      };

      console.log('Creating token with data:', tokenData);
      let tokenResponse;
      try {
        tokenResponse = await createTokenMutation.mutateAsync(tokenData);
        console.log('Token creation response:', tokenResponse);
        if (!tokenResponse) {
          throw new Error('Token response was undefined');
        }
      } catch (err) {
        console.error('Failed to create token:', err);
        throw new Error(
          `Failed to create token: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      }

      // Get users to CC
      const ccList: string[] = [];

      // Add self to CC if selected
      if (data.selfCC && loggedUser.user.email) {
        ccList.push(loggedUser.user.email);
      }

      // Add selected employees to CC
      if (data.employeesCC && data.employeesCC.length > 0) {
        ccList.push(...data.employeesCC);
      }

      // Update the quote document with the inPerson field and adjusted expiration date BEFORE sending the proposal
      if (quoteDocument) {
        const fieldMask = ['InPerson', 'BlockLineItems'];
        const updatedQuoteDocFields: Partial<QuoteDocument> = {
          ...quoteDocument,
        };

        // If inPerson flag is true, update the in_person field
        if (inPerson) {
          updatedQuoteDocFields.inPerson = 1;
        } else {
          updatedQuoteDocFields.inPerson = 0;
        }

        // Always save blockLineItems from clientInfo (includes cached preference)
        updatedQuoteDocFields.blockLineItems = clientInfo.blockLineItems ? 1 : 0;

        // Update the expiration date if it was adjusted
        if (adjustedExpirationDate && adjustedExpirationDate !== clientInfo.quoteExpireDate) {
          updatedQuoteDocFields.quoteExpireDate = adjustedExpirationDate;
          fieldMask.push('QuoteExpireDate');
        }

        const updatedQuoteDoc = QuoteDocument.create({
          ...updatedQuoteDocFields,
          fieldMask,
        });

        const updatedDoc = await updateQuoteDocumentMutation.mutateAsync(updatedQuoteDoc);
        if (updatedDoc) {
          await refreshQuoteDocument();
        }
      }

      // Prepare and send the email request
      const sendProposalRequest = NewSendProposalDocumentRequest.create({
        jobId: serviceItem.id,
        quoteDocumentId: quoteDocument.id,
        ccEmails: ccList,
      });

      console.log('Sending proposal email with data:', sendProposalRequest);
      try {
        const result = await ProposalClientService.NewSendProposalDocument(sendProposalRequest);
        console.log('Proposal email send result:', result);

        // Update the quote document with the sent date AFTER sending the proposal
        if (quoteDocument) {
          const now = timestamp();
          const fieldMask = ['QuoteSentDate', 'SignedDate'];
          const updatedQuoteDocFields: Partial<QuoteDocument> = {
            ...quoteDocument,
            quoteSentDate: now,
            signedDate: NULL_TIME, // Reset signature date to NULL_TIME when sending proposal
          };

          const updatedQuoteDoc = QuoteDocument.create({
            ...updatedQuoteDocFields,
            fieldMask,
          });

          const updatedDoc = await updateQuoteDocumentMutation.mutateAsync(updatedQuoteDoc);
          if (updatedDoc) {
            await refreshQuoteDocument();
          }
        }

        // Log the activity
        await ActivityLogClientService.Create(
          ActivityLog.create({
            eventId: serviceItem.id,
            userId: loggedUser.user.id,
            activityName: 'Proposal Sent',
          }),
        );

        // Show appropriate toast based on flow
        if (inPerson) {
          toast({
            title: 'Success',
            description: 'Proposal saved and shared in-person',
            variant: 'success',
          });
          // Open a new tab with the customer proposal view
          const proposalUrl = `/customer-proposal-preview/${quoteDocument.id}`;
          window.open(proposalUrl, '_blank');
        } else {
          toast({
            title: 'Success',
            description: 'Proposal has been sent to the customer',
            variant: 'success',
          });
        }
      } catch (err) {
        console.error('Error sending proposal email:', err);
        toast({
          title: 'Error',
          description: 'Failed to send the proposal email. Please try again.',
          variant: 'destructive',
        });
        throw err;
      }

      if (reload) {
        reload();
      }

      setCurrentStep('services');
    } catch (err) {
      console.error('Error sending proposal:', err);
      toast({
        title: 'Error',
        description: 'An error occurred while sending the proposal.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to generate shareable link to proposal tab
  const generateProposalLink = useCallback(() => {
    const proposalTabIndex = TAB_VALUE_TO_INDEX_MAP['NewProposal'];
    const currentUrl = window.location.origin;
    const proposalUrl = `${currentUrl}/jobs/${serviceItem.id}?defaultTab=${proposalTabIndex}`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(proposalUrl)
      .then(() => {
        toast({
          title: 'Link Copied!',
          description: 'Proposal link has been copied to clipboard.',
          variant: 'success',
        });
      })
      .catch(() => {
        toast({
          title: 'Copy Failed',
          description: 'Failed to copy link to clipboard.',
          variant: 'destructive',
        });
      });
  }, [serviceItem.id]);

  // Create the Generate Link button to match status card download buttons
  const generateLinkButton = (
    <button
      onClick={generateProposalLink}
      className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
    >
      <ExternalLinkIcon className="h-4 w-4" />
      Generate Link to Page
    </button>
  );

  // Handler to open Quick Update modal
  const handleOpenQuickUpdate = useCallback(() => {
    setIsQuickUpdateOpen(true);
  }, []);

  // Handler to close Quick Update modal
  const handleCloseQuickUpdate = useCallback(() => {
    setIsQuickUpdateOpen(false);
  }, []);

  // Handler when Quick Update is completed
  const handleQuickUpdateComplete = useCallback(() => {
    setIsQuickUpdateOpen(false);
    if (reload) {
      reload();
    }
  }, [reload]);

  if (isQuoteLineLoading || isQuoteDocLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-6">
        <div className="mb-2 flex items-center justify-center">
          <LoadingIcon className="h-10 w-10 text-gray-500" />
        </div>
        <div className="text-gray-500">Loading services...</div>
      </div>
    );
  }

  if (isContextLoading) {
    return (
      <div className={className}>
        <div className="flex min-h-[200px] items-center justify-center">
          <LoadingIcon />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Render Quick Update Dialog when open */}
      <Dialog open={isQuickUpdateOpen} onOpenChange={handleCloseQuickUpdate}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Proposal Quick Update</DialogTitle>
          </DialogHeader>
          <ProposalQuickUpdate
            serviceCallId={serviceItem.id.toString()}
            customerId={customer.id}
            quoteDocument={quoteDocument}
            onComplete={handleQuickUpdateComplete}
            onCancel={handleCloseQuickUpdate}
          />
        </DialogContent>
      </Dialog>

      {/* Render normal workflow when Quick Update is not open */}
      {!isQuickUpdateOpen && (
        <ProposalWorkflow
          currentStep={currentStep}
          onNext={moveToNextStep}
          onBack={moveToPreviousStep}
          disableContinue={
            (currentStep === 'services' && !isServicesValid) ||
            (currentStep === 'review' && isPhotosBusy) ||
            isSubmitting
          }
          servicesContent={
            <>
              {quoteDocument?.quoteSentDate &&
                quoteDocument.quoteSentDate !== '0001-01-01 00:00:00' &&
                quoteDocument.quoteSentDate !== '' &&
                currentStep === 'services' && (
                  <ProposalStatus
                    customer={{
                      name:
                        `${customer.firstname || ''} ${customer.lastname || ''}`.trim() || 'N/A',
                      email: customer.email,
                    }}
                    proposalId={`${serviceItem.id}`}
                    sentTimestamp={quoteDocument.quoteSentDate}
                    signedTimestamp={
                      quoteDocument.signedDate != undefined && quoteDocument.signedDate != NULL_TIME
                        ? quoteDocument.signedDate
                        : undefined
                    }
                    quoteDocument={quoteDocument}
                    jobStatus={serviceItem.logJobStatus}
                    generateLinkButton={generateLinkButton}
                    onQuickUpdate={handleOpenQuickUpdate}
                    hasApprovedLineItems={hasApprovedLineItems}
                  />
                )}
              <ServicesStep
                selectedServices={quoteLines}
                onAddService={handleAddQuoteLine}
                onUpdateService={handleUpdateQuoteLine}
                onDeleteService={handleDeleteQuoteLine}
                serviceCallId={serviceItem.id.toString()}
                onValidationChange={setIsServicesValid}
                serviceItem={localServiceItem}
                quoteDocument={quoteDocument}
                onDiscountChange={handleDiscountChange}
                isProposalAdmin={isProposalAdmin}
              />
            </>
          }
          reviewContent={
            <ReviewStep
              selectedServices={quoteLines}
              onClientInfoChange={handleClientInfoChange}
              clientInfo={clientInfo}
              serviceCallId={serviceItem.id.toString()}
              quoteDocument={quoteDocument}
              customer={customer}
              property={property}
              loggedUserId={loggedUser.user.id}
              serviceItem={localServiceItem}
              onPhotosBusyChange={setIsPhotosBusy}
            />
          }
          settingsContent={
            <ProposalReviewAndSend
              proposalFileName={fileName}
              entries={quoteLines}
              onSubmit={handleSubmitProposal}
              onCancel={moveToPreviousStep}
              loading={isSubmitting}
              disabled={isSubmitting}
              clientInfo={clientInfo}
              quoteDocument={quoteDocument}
              customer={customer}
              onClientInfoChange={handleClientInfoChange}
              onExpirationDateChange={handleExpirationDateChange}
              onValidUntilDateChange={handleValidUntilDateChange}
              serviceItem={localServiceItem}
            />
          }
        />
      )}
    </div>
  );
};
